/**
 * FinderSplitPane Component
 *
 * Resizable sidebar + content pane layout.
 * Persists sidebar width to localStorage.
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import './finder.css'

interface FinderSplitPaneProps {
  sidebar: React.ReactNode
  content: React.ReactNode
  preview?: React.ReactNode

  sidebarWidth?: number
  previewWidth?: number
  minSidebarWidth?: number
  maxSidebarWidth?: number
  minPreviewWidth?: number
  maxPreviewWidth?: number

  storageKey?: string
}

const DEFAULT_SIDEBAR_WIDTH = 180
const MIN_SIDEBAR_WIDTH = 120
const MAX_SIDEBAR_WIDTH = 300
const DEFAULT_PREVIEW_WIDTH = 250
const MIN_PREVIEW_WIDTH = 200
const MAX_PREVIEW_WIDTH = 400

export function FinderSplitPane({
  sidebar,
  content,
  preview,
  sidebarWidth: initialSidebarWidth,
  previewWidth: initialPreviewWidth,
  minSidebarWidth = MIN_SIDEBAR_WIDTH,
  maxSidebarWidth = MAX_SIDEBAR_WIDTH,
  minPreviewWidth = MIN_PREVIEW_WIDTH,
  maxPreviewWidth = MAX_PREVIEW_WIDTH,
  storageKey,
}: FinderSplitPaneProps) {
  // Load initial widths from localStorage or use defaults
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    if (storageKey && typeof window !== 'undefined') {
      const stored = localStorage.getItem(`${storageKey}-sidebar`)
      if (stored) return parseInt(stored, 10)
    }
    return initialSidebarWidth ?? DEFAULT_SIDEBAR_WIDTH
  })

  const [previewWidth, setPreviewWidth] = useState(() => {
    if (storageKey && typeof window !== 'undefined') {
      const stored = localStorage.getItem(`${storageKey}-preview`)
      if (stored) return parseInt(stored, 10)
    }
    return initialPreviewWidth ?? DEFAULT_PREVIEW_WIDTH
  })

  const [isResizingSidebar, setIsResizingSidebar] = useState(false)
  const [isResizingPreview, setIsResizingPreview] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Persist width changes to localStorage
  useEffect(() => {
    if (storageKey && typeof window !== 'undefined') {
      localStorage.setItem(`${storageKey}-sidebar`, sidebarWidth.toString())
    }
  }, [sidebarWidth, storageKey])

  useEffect(() => {
    if (storageKey && typeof window !== 'undefined') {
      localStorage.setItem(`${storageKey}-preview`, previewWidth.toString())
    }
  }, [previewWidth, storageKey])

  // Handle sidebar resize
  const handleSidebarMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizingSidebar(true)
  }, [])

  // Handle preview resize
  const handlePreviewMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizingPreview(true)
  }, [])

  // Global mouse move/up handlers
  useEffect(() => {
    if (!isResizingSidebar && !isResizingPreview) return

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return

      const containerRect = containerRef.current.getBoundingClientRect()

      if (isResizingSidebar) {
        const newWidth = e.clientX - containerRect.left
        setSidebarWidth(
          Math.max(minSidebarWidth, Math.min(maxSidebarWidth, newWidth))
        )
      }

      if (isResizingPreview) {
        const newWidth = containerRect.right - e.clientX
        setPreviewWidth(
          Math.max(minPreviewWidth, Math.min(maxPreviewWidth, newWidth))
        )
      }
    }

    const handleMouseUp = () => {
      setIsResizingSidebar(false)
      setIsResizingPreview(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [
    isResizingSidebar,
    isResizingPreview,
    minSidebarWidth,
    maxSidebarWidth,
    minPreviewWidth,
    maxPreviewWidth,
  ])

  return (
    <div
      ref={containerRef}
      className={`finder-split-pane ${isResizingSidebar || isResizingPreview ? 'resizing' : ''}`}
    >
      {/* Sidebar */}
      {sidebar && (
        <>
          <div
            className="finder-split-pane__sidebar"
            style={{ width: sidebarWidth }}
          >
            {sidebar}
          </div>
          <div
            className="finder-split-pane__resize-handle"
            onMouseDown={handleSidebarMouseDown}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize sidebar"
          />
        </>
      )}

      {/* Main content */}
      <div className="finder-split-pane__content">{content}</div>

      {/* Preview pane */}
      {preview && (
        <>
          <div
            className="finder-split-pane__resize-handle"
            onMouseDown={handlePreviewMouseDown}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize preview"
          />
          <div
            className="finder-split-pane__preview"
            style={{ width: previewWidth }}
          >
            {preview}
          </div>
        </>
      )}
    </div>
  )
}

export default FinderSplitPane
